import React, { createContext, useContext, ReactNode } from 'react'
import { useQuery, QueryClient, QueryClientProvider, UseQueryResult, QueryKey } from '@tanstack/react-query'
import { get } from 'aws-amplify/api'
import { getValidAuthToken } from '../utils/auth'
import logger from '../services/logger'

export interface ApiResponse<T> {
  message?: string;
  resultUrl?: string;
  data?: T
}

type ApiQueryResult<T> = T | ApiResponse<T>;

interface ApiContextType {
  fetchApi: <T>(
    apiName: string,
    path: string,
    params: Record<string, string>
  ) => Promise<T | ApiResponse<T>>;
  useApiQuery: <T>(
    apiName: string,
    path: string,
    params: Record<string, string>,
    enabled: boolean
  ) => UseQueryResult<ApiQueryResult<T>, Error>;
}

const ApiContext = createContext<ApiContextType | null>(null);

export class ApiRequestError extends Error {
  statusCode?: number;
  code?: string;

  constructor(message: string, statusCode?: number, code?: string) {
    super(message)
    this.name = 'ApiRequestError'
    this.statusCode = statusCode
    this.code = code
  }
}

export const useApi = () => {
  const context = useContext(ApiContext)
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider')
  }
  return context
}

const parseBody = async (body: any): Promise<any> => {
  if (body instanceof ReadableStream) {
    const reader = body.getReader()
    const decoder = new TextDecoder()
    let result = ''
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      result += decoder.decode(value, { stream: true })
    }
    result += decoder.decode()
    return result ? JSON.parse(result) : null
  }
  if (body && typeof body.json === 'function') {
    return body.json()
  }
  return body
}

const parseErrorBody = async (body: unknown): Promise<any> => {
  if (typeof body === 'string') return body ? JSON.parse(body) : null
  return parseBody(body)
}

const requestData = async <T,>(
  apiName: string,
  path: string,
  params: Record<string, string>,
): Promise<T | ApiResponse<T>> => {
  try {
    const authToken = await getValidAuthToken()
    const restOperation = get({
      apiName,
      path,
      options: {
        headers: { Authorization: authToken || '' },
        queryParams: params
      }
    })

    const response = await restOperation.response

    if (response.body instanceof ReadableStream || (response.body && typeof (response.body as any).json === 'function')) {
      const result = await parseBody(response.body)

      // Check if the response contains resultUrl (S3 redirection)
      if (result && typeof result === 'object' && 'resultUrl' in result) {
        return result as ApiResponse<T>;
      }

      return result as T
    } else {
      return response as T
    }

  } catch (err: any) {
    const statusCode = err?.response?.statusCode
    let errorBody: any = null
    try {
      errorBody = await parseErrorBody(err?.response?.body)
    } catch {
      // The response body is optional and may already have been consumed.
    }
    const code = errorBody?.code
    const message = errorBody?.message || errorBody?.error || 'Failed to fetch data'
    await logger.log(`API request failed: status=${statusCode ?? 'unknown'} code=${code ?? 'unknown'}`, {
      source: { file: __filename },
    })
    throw new ApiRequestError(message, statusCode, code)
  }
}

const fetchData = async <T,>(queryKey: QueryKey): Promise<T | ApiResponse<T>> => {
  const [apiName, path, params] = queryKey as [string, string, Record<string, string>]
  return requestData<T>(apiName, path, params)
}

const queryClient = new QueryClient();
export const ApiProvider: React.FC<{ children: ReactNode }> = ({ children }) => {

  const useApiQuery = <T,>(
    apiName: string,
    path: string,
    params: Record<string, string>,
    enabled: boolean
  ): UseQueryResult<ApiQueryResult<T>, Error> => {
    return useQuery({
      queryKey: [apiName, path, params],
      queryFn: () => fetchData<T>([apiName, path, params]),
      enabled,
      // Amplify REST already retries transient failures up to three total
      // attempts. A React Query retry would repeat that entire sequence.
      retry: false,
      staleTime: 0,
      // staleTime: 1 * 60 * 1000, // 1 minute
      // A results page that mounts again (Back to List from a trip's
      // detail page) shows the rows it left instead of refetching them; a
      // key with no cached rows still loads, and Find refetches explicitly.
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    });
  };

  const contextValue: ApiContextType = {
    fetchApi: requestData,
    useApiQuery
  };

  return (
    <QueryClientProvider client={queryClient}>
      <ApiContext.Provider value={contextValue}>
        {children}
      </ApiContext.Provider>
    </QueryClientProvider>
  );
};
